import { resolvePlanningBufferPct } from '@/utils/planningBuffer';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
// Static (FLOYD 2026-10-26): a lazily loaded chunk is removed by the next deploy from under an open tab.
import { syncTicketBreakRowsWithSchedule } from '@/utils/syncTicketBreakRows';
import { useToast } from '@/hooks/use-toast';
import { addMinutes } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { getShopDayLabel } from '@/utils/shopTime';
import {
  calculateRemainingMinutes,
  parseBreaksForDate,
  ticketSpansBreak,
  computeBreakBufferAction,
  consumeBreakBuffer,
} from '@/utils/breakBufferHelpers';
import { getMinutesInShopTZ } from '@/utils/timezoneMath';
import { pushClientError } from '@/utils/clientErrorBuffer';
// Core-100 R4 (CORE-AC): the finishes this hook used to compute and write are DERIVED BY THE SERVER
// (`ticket-finish-intent`); the hook decides WHEN to ask and still drives the chain authority and the
// cascades. The table refuses a browser-written finish.
import { requestServerFinish } from '@/utils/serverFinishIntent';
// DTC-F: route the pushback "now" anchor through the canonical clock so
// timer-pushback decisions are deterministic under `clock.setFixed(...)`.
// All downstream comparisons (`now > optiFinish`, `now.getTime() - ...`)
// continue to use this single anchor unchanged.
import { clock } from '@/utils/clock';
// VIS-A1: stagger the recurring 60s tick by 20s relative to its sibling
// 60s polling hooks (useAutoBump @ offset 0, useConvertedRolloverGrowth @
// offset 40s). Without this stagger all three siblings — which mount in the
// same dashboard render cycle, run identical 60s setIntervals, and share a
// `document.hidden` early-return gate — fire within milliseconds of each
// other on every minute boundary AND on every tab refocus after a hidden
// period, saturating shop_profile + tickets + cascade chains. With this
// stagger their setInterval phases are pinned 20s apart at steady state and
// never co-occur even after a multi-hour hidden period.
import { createStaggeredInterval, VIS_A1_STAGGER } from '@/utils/staggeredInterval';
// R3-A OPTION B (founder decision 2026-10-21): the SERVER runs this pushback rule (Edge function run-long-autobump,
// after AutoBump's rules, through ticket-finish-intent's derivation). When it answered — usually AutoBump's request
// 20 s earlier, reused — this hook does nothing; on an error / an older server it runs unchanged.
import { requestRunLongReconcile, serverOwnsRunLong } from '@/lib/scheduling/runLongReconcileClient';

// FIX-R132-MED-6: helper to surface silent ticket-update failures inside
// useOptiFinishPushback to support's bug-report attachments. The R35-HIGH-5
// fix only covered the most common normal-update branch (line ~257); the
// EOD-lock + raw-fallback branches still vanished if the update failed.
function reportPushbackFailure(label: string, ticketId: string, shopId: string | null, err: unknown) {
  const errMsg = (err as { message?: string } | undefined)?.message ?? String(err);
  console.error(`[OptiFinishPushback] ${label}:`, err, { ticketId, shopId });
  pushClientError({
    source: 'mutation',
    message: `[OptiFinishPushback] ${label}: ${errMsg}`,
    extra: { ticketId, label },
    shopId,
  });
}

interface Ticket {
  id: string;
  status: string;
  timer_status?: string;
  start_time: string;
  scheduled_date?: string;
  opti_finish?: string;
  predicted_minutes?: number;
  actually_started?: boolean;
  actual_start_time?: string;
  assigned_tech_id?: string;
  customer_name?: string | null;
  break_buffer_remaining?: number | null;
  shop_id?: string;
  rollover_of_ticket_id?: string | null;
  parent_ticket_id?: string | null;
  sequence_index?: number | null;
  split_reason?: string | null;
}

/**
 * Hook to automatically push back opti_finish when timers run past the scheduled finish time
 * Runs every minute to check active tickets and update opti_finish if needed
 * 
 * UNIFIED BREAK-BUFFER LOGIC:
 * - Same behavior for both running and paused tickets
 * - Case A: remainingMinutes >= breakDuration → jump opti_finish forward past break
 * - Case B: remainingMinutes < breakDuration → consume break_buffer_remaining before pushing
 */
// BUG-8: Accept shopId to scope ticket queries to current shop only
export const useOptiFinishPushback = (selectedDayLabel: string, shopId: string | null) => {
  const { toast } = useToast();

  useEffect(() => {
    // VIS-A1: holds the cleanup function returned by createStaggeredInterval
    // (manages BOTH the deferred startup timer and the eventual setInterval).
    let cleanupTick: (() => void) | null = null;

    const checkAndPushbackOptiFinish = async () => {
      try {
        // V-E-2 R152-MED-1: skip when the tab is hidden. checkAndPushbackOptiFinish
        // fires a chain (shop_profile + tickets + ticket_pause_sessions per ticket)
        // every 60s; running it on background tabs wastes DB hits at multi-tenant
        // scale. The interval keeps firing — we just early-return until the tab
        // is visible again, so resume is automatic on tab refocus.
        if (typeof document !== 'undefined' && document.hidden) return;
        const dateStr = selectedDayLabel;
        // BUG-8: Abort if no shopId — prevents cross-shop ticket processing
        if (!shopId) return;
        if (serverOwnsRunLong(await requestRunLongReconcile(shopId, { maxAgeMs: 55_000 }))) return;
        const now = clock.now();
        
        // Get shop profile for EOD settings and timezone
        const { data: shopProfile, error: profileError } = await supabase
          .from('shop_profile')
      .select('shop_culture, time_zone')
      .eq('shop_id', shopId)
      .maybeSingle();

        if (profileError) {
          console.warn('[OptiFinishPushback] Could not fetch shop profile:', profileError);
          return;
        }
        
        const shopCulture = (shopProfile?.shop_culture as any) || {};
        const willingnessRollPastEOD = shopCulture.willingness_roll_past_eod || 0;
        const timezone = shopProfile?.time_zone || 'America/Los_Angeles';
        
        // Get all tickets with running or paused timers (UNIFIED behavior)
        const { data: tickets, error: ticketsError } = await supabase
          .from('tickets')
          .select('id, status, timer_status, start_time, scheduled_date, opti_finish, predicted_minutes, actually_started, actual_start_time, assigned_tech_id, customer_name, break_buffer_remaining, shop_id, rollover_of_ticket_id, parent_ticket_id, sequence_index, split_reason')
          .eq('scheduled_date', dateStr)
          .eq('shop_id', shopId)    // BUG-8: Scope to current shop only
          .eq('parking_lot', false) // LOW 4.5: Exclude parking lot tickets
          .in('timer_status', ['running', 'paused'])
          .eq('actually_started', true)
          .not('actual_start_time', 'is', null)
          .not('opti_finish', 'is', null)
          .not('assigned_tech_id', 'is', null) as { data: Ticket[] | null; error: any };

        if (ticketsError) {
          console.error('[OptiFinishPushback] Error fetching tickets:', ticketsError);
          return;
        }

        if (!tickets || tickets.length === 0) return;

        // Check each ticket to see if timer has run past opti_finish
        for (const ticket of tickets) {
          const optiFinish = new Date(ticket.opti_finish!);
          
          // If current time is past opti_finish, apply pushback logic
          if (now > optiFinish) {
            const minutesPast = Math.ceil((now.getTime() - optiFinish.getTime()) / 60000);
            
            // Fetch technician data for breaks and efficiency
            const { data: tech } = await supabase
              .from('technicians')
              .select('name, shift_end, efficiency_percentage, buffer_percentage, recurring_breaks, non_recurring_breaks')
              .eq('id', ticket.assigned_tech_id)
              .single();

            if (!tech) continue;

            // Parse breaks for this date
            const breaks = parseBreaksForDate(
              tech.recurring_breaks as any,
              tech.non_recurring_breaks as any,
              dateStr
            );

            // Get current opti_finish in minutes from midnight
            const optiFinishMin = getMinutesInShopTZ(ticket.opti_finish!, timezone);
            const startMin = getMinutesInShopTZ(ticket.start_time, timezone);

            // Check if ticket is spanning or entering a break
            const spannedBreak = ticketSpansBreak(startMin, optiFinishMin + minutesPast, breaks);

            let newBreakBufferRemaining: number | null = ticket.break_buffer_remaining ?? null;
            let actualPushMinutes = minutesPast;

            if (spannedBreak) {
              // Check if we're newly entering the break or already in it
              const wasSpanningBefore = ticketSpansBreak(startMin, optiFinishMin, breaks);
              
              if (!wasSpanningBefore) {
                // Newly entering break - apply Case A or Case B logic
                const remainingMinutes = await calculateRemainingMinutes(
                  ticket.id,
                  ticket.predicted_minutes || 0,
                  tech.efficiency_percentage || 100,
                  resolvePlanningBufferPct(tech.buffer_percentage)
                );

                const bufferAction = computeBreakBufferAction(
                  remainingMinutes,
                  spannedBreak.durationMinutes,
                  ticket.break_buffer_remaining
                );

                if (bufferAction.case === 'A') {
                  // Case A: Jump opti_finish past the break
                  actualPushMinutes = minutesPast + bufferAction.jumpMinutes;
                  newBreakBufferRemaining = null;
                  console.log(`[OptiFinishPushback] Case A: Jumping ${ticket.id} past break by ${bufferAction.jumpMinutes} min`);
                } else if (bufferAction.case === 'B') {
                  // Case B: Set buffer, consume before pushing
                  newBreakBufferRemaining = bufferAction.breakBufferRemaining;
                  const consumed = consumeBreakBuffer(bufferAction.breakBufferRemaining, minutesPast);
                  actualPushMinutes = consumed.pushMinutes;
                  newBreakBufferRemaining = consumed.remainingBuffer > 0 ? consumed.remainingBuffer : null;
                  console.log(`[OptiFinishPushback] Case B: Consuming buffer for ${ticket.id}, remaining=${newBreakBufferRemaining}`);
                }
              } else if (newBreakBufferRemaining && newBreakBufferRemaining > 0) {
                // Already spanning break with active buffer - consume buffer
                const consumed = consumeBreakBuffer(newBreakBufferRemaining, minutesPast);
                actualPushMinutes = consumed.pushMinutes;
                newBreakBufferRemaining = consumed.remainingBuffer > 0 ? consumed.remainingBuffer : null;
                console.log(`[OptiFinishPushback] Consuming existing buffer for ${ticket.id}, remaining=${newBreakBufferRemaining}`);
              }
            } else {
              // Not spanning any break - clear buffer if exists
              newBreakBufferRemaining = null;
            }

            // Calculate new opti_finish
            const newOptiFinish = new Date(optiFinish.getTime() + actualPushMinutes * 60000);
            
            // Check if this ticket has a rollover child (canonical: rollover_of_ticket_id)
            const { data: rolloverTickets } = await supabase
              .from('tickets')
              .select('id, predicted_minutes, start_time')
              .eq('rollover_of_ticket_id', ticket.id)
              // DMC-D3b: canonical active-ticket filter — both reps
              // excluded. Status IN excludes terminal states; deleted_at
              // null is the defense-in-depth ghost-row guard.
              .not('status', 'in', '("Completed","Cancelled","Deleted")')
              .is('deleted_at', null)
              .order('scheduled_date', { ascending: false })
              .limit(1);
            
            const rolloverTicket = rolloverTickets?.[0];
            const hasRollover = !!rolloverTicket;
            
            // Check EOD rollover
            if (tech.shift_end) {
              // BUG-3: Parse shift_end in shop timezone, not viewer machine timezone
              const shiftEnd = fromZonedTime(`${dateStr}T${tech.shift_end}`, timezone);
              const maxAllowedEnd = addMinutes(shiftEnd, willingnessRollPastEOD);
              
              if (hasRollover && rolloverTicket) {
                // HAS ROLLOVER: Extend the rollover ticket's predicted_minutes and recalc
                // Lock this ticket at tech's shift end (strict EOD)
                // The pushed minutes this ticket cannot do today are more future
                // work for its chain. oct20d (PR #13 round 4, item A): the browser no longer adds
                // them to the chain. Chain growth is the server's durable ledger alone (run-long /
                // pause minutes claimed by a compare-and-swap of their watermark, grown exactly
                // once); the server's planner already excluded this hook's chain add (it re-added
                // the minutes past an end-of-day lock on every tick). This fallback only locks the
                // finish at the end of day — it moves no watermark, so nothing the server will
                // claim is lost and nothing is added twice.
                // CSI-I2-A2 → Core-100 R4 (CORE-AC): the rollover EOD-lock is the server's 'pushback'
                // derivation with lockAtEod — finish = the technician's dated end of day (never before
                // the start), break_buffer_remaining from the same break-buffer rule. The bespoke
                // `triggerBoardCascade` below fires only after the lock succeeds.
                const mutateResult = await requestServerFinish(ticket.id, 'pushback', {
                  reason: 'opti_finish_pushback_rollover_eod_lock',
                  params: { lockAtEod: true },
                });
                if (mutateResult.refused) continue;
                const updateError = mutateResult.success ? null : { message: mutateResult.error };

                if (!updateError) {
                  const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
                  const ticketShopId = ticket.shop_id;
                  if (ticketShopId) {
                    await triggerBoardCascade(ticketShopId, dateStr, ticket.assigned_tech_id);
                  }
                  console.log(`[OptiFinishPushback] Ticket ${ticket.id} locked at EOD ${tech.shift_end}; chain growth for rollover ${rolloverTicket.id} is the server ledger's`);
                } else {
                  // FIX-R132-MED-6
                  reportPushbackFailure('EOD-lock update failed', ticket.id, ticket.shop_id ?? null, updateError);
                }
                continue; // Skip normal update flow
              } else if (newOptiFinish > maxAllowedEnd) {
                const rolloverMinutes = Math.ceil((newOptiFinish.getTime() - shiftEnd.getTime()) / 60000);
                
                toast({
                  title: '⚠️ Run-Long Causing Rollover',
                  description: `${ticket.customer_name || 'Ticket'} on ${tech.name} will exceed EOD by ${rolloverMinutes} min. Consider reassigning to available tech.`,
                  variant: 'destructive',
                  duration: 10000,
                });
                
                console.log(`[Run-Long] Ticket ${ticket.id} causing ${rolloverMinutes} min rollover on ${tech.name}`);
              }
            }
            
            // Update the ticket with unified break-buffer handling (only if no rollover).
            // V-A-6 → Core-100 R4 (CORE-AC): the server's 'pushback' derivation pushes the finish by
            // the overrun (Case A / Case B break buffer) and grows predicted_minutes by the push, in
            // one write. The bespoke post-update side effects below — break-row sync, scoped
            // triggerBoardCascade, and cascadeRolloverChainAfterPushback — still follow.
            const mutateResult = await requestServerFinish(ticket.id, 'pushback', {
              reason: 'opti_finish_pushback_normal',
            });
            if (mutateResult.refused) continue;
            const updateError = mutateResult.success ? null : { message: mutateResult.error };

            if (updateError) {
              // FIX-R35-HIGH-5: surface cascade failure so the operator knows
              // the board is in a drift state. Previously this was
              // console-only, which meant a silently-failed opti_finish
              // update would propagate wrong predicted times to downstream
              // tickets without anyone noticing until a scheduling error.
              console.error(
                '[OptiFinishPushback] ticket update failed',
                { ticketId: ticket.id, shopId: ticket.shop_id, error: updateError }
              );
              toast({
                title: '⚠️ Schedule update failed',
                description: `Could not push back finish time for ${ticket.customer_name || ticket.id}. The board may be out of sync until next refresh.`,
                variant: 'destructive',
                duration: 8000,
              });
            } else {
              // Cascade downstream tickets + sync break rows
              try {
                
                // R5-L-2: Use getShopDayLabel instead of UTC split — correct for UTC+ shops
                const ticketDateStr = ticket.scheduled_date || 
                  (ticket.start_time ? getShopDayLabel(ticket.start_time, timezone) : dateStr);
                
                // Sync break rows to ensure exactly one ticket owns each break
                await syncTicketBreakRowsWithSchedule({
                  techId: ticket.assigned_tech_id!,
                  dateStr: ticketDateStr,
                });
                
                // Trigger global cascade as single authority for downstream chaining
                try {
                  const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
                  const ticketShopId = ticket.shop_id;
                  if (ticketShopId && ticketDateStr) {
                    await triggerBoardCascade(ticketShopId, ticketDateStr, ticket.assigned_tech_id);
                  }
                } catch (cascadeErr) {
                  console.error('[OptiFinishPushback] Cascade trigger failed:', cascadeErr);
                }
                
                console.log(`[OptiFinishPushback] Cascaded schedule and synced break rows for tech ${ticket.assigned_tech_id}`);
              } catch (cascadeError) {
                console.error('[OptiFinishPushback] Error cascading schedule:', cascadeError);
              }

              // R39 FIX-6: Cross-day rollover chain cascade (self-cascading — no external cascade needed)
              try {
                const { cascadeRolloverChainAfterPushback, isPartOfRolloverChain } = 
                  await import('@/utils/rolloverChain');
                
                const ticketForCheck = {
                  parent_ticket_id: ticket.parent_ticket_id,
                  sequence_index: ticket.sequence_index,
                  rollover_of_ticket_id: ticket.rollover_of_ticket_id,
                  split_reason: ticket.split_reason,
                };
                
                if (isPartOfRolloverChain(ticketForCheck)) {
                  const ticketShopId = ticket.shop_id;
                  
                  if (ticketShopId) {
                    const cascadeResult = await cascadeRolloverChainAfterPushback(
                      ticket.id,
                      ticketShopId
                    );
                    
                    if (cascadeResult.segmentsUpdated > 0) {
                      console.log(`[OptiFinishPushback] Cascaded ${cascadeResult.segmentsUpdated} rollover segments (self-cascading)`);
                    }
                    
                    // Handle new EOD violations triggered by chain cascade.
                    // ticketType lets the EODRolloverModal hide "Convert to
                    // Rollover" for Waiters. Chain-violation upstream sources
                    // are seldom Waiters (Waiters typically don't roll), but
                    // forwarding the type when present keeps the contract
                    // consistent across all eod-exceeded dispatch sites.
                    for (const violation of cascadeResult.newEodViolations) {
                      window.dispatchEvent(new CustomEvent('eod-exceeded', {
                        detail: {
                          ticketId: violation.ticketId,
                          customerName: violation.customerName,
                          techName: violation.techName,
                          rolloverMinutes: violation.rolloverMinutes,
                          dateStr: violation.dateStr,
                          isChainViolation: true,
                          ticketType: (violation as any).ticketType ?? null,
                        }
                      }));
                    }
                  }
                }
              } catch (chainError) {
                console.error('[OptiFinishPushback] Error cascading rollover chain:', chainError);
              }
            }
          } else if (ticket.timer_status === 'paused' && ticket.actually_started && ticket.actual_start_time) {
            // PAUSED TICKET PROJECTED FINISH CHECK
            // When a ticket is paused, pause time accumulates but the stored opti_finish
            // doesn't reflect it until now > optiFinish. This causes downstream tickets
            // to chain from a stale opti_finish.
            //
            // Fix: Calculate projected finish including accumulated pause time.
            // If projected > stored, update DB immediately so cascade picks it up.

            // Fetch total worked seconds from timer sessions
            const { data: sessions } = await supabase
              .from('ticket_timer_sessions')
              .select('duration_min')
              .eq('ticket_id', ticket.id);

            const workedMin = (sessions || []).reduce(
              (sum: number, s: any) => sum + (s.duration_min || 0),
              0
            );

            const actualStart = new Date(ticket.actual_start_time);
            const wallClockMin = (now.getTime() - actualStart.getTime()) / 60000;
            const pauseMin = Math.max(0, wallClockMin - workedMin);

            if (pauseMin < 1) continue; // No meaningful pause accumulated

            const optiTime = ticket.predicted_minutes || 0;
            // Projected finish = actual start + optiTime + accumulated pause
            const projectedFinish = new Date(
              actualStart.getTime() + (optiTime + pauseMin) * 60000
            );

            const storedFinish = optiFinish;

            // Only push if projected exceeds stored by at least 1 minute
            const driftMin = Math.ceil(
              (projectedFinish.getTime() - storedFinish.getTime()) / 60000
            );

            if (driftMin >= 1) {
              console.log(
                `[OptiFinishPushback] Paused ticket ${ticket.id}: pause=${Math.round(pauseMin)}min, ` +
                `projected=${projectedFinish.toISOString()}, stored=${storedFinish.toISOString()}, drift=+${driftMin}min`
              );

              // FIX-1 R47: For chain tickets, the chain follows through the chain authority
              const { isPartOfRolloverChain, cascadeRolloverChainAfterPushback } = await import('@/utils/rolloverChain');
              const ticketCtx = { parent_ticket_id: ticket.parent_ticket_id, rollover_of_ticket_id: ticket.rollover_of_ticket_id };

              if (isPartOfRolloverChain(ticketCtx)) {
                // A pause is not new work: the paused row (history) simply ends
                // later. Record its projected finish on its own row, then let
                // the chain authority re-plan its unresolved future from that
                // finish. Its planned minutes are never rewritten from a
                // projected duration.
                const startTime = ticket.start_time ? new Date(ticket.start_time) : null;
                if (startTime) {
                  try {
                    // Core-100 R4 (CORE-AC): the server's 'pause_extend' derivation (the ONE paused
                    // finish: now + the work that remains — this hook used book minutes, AutoBump
                    // OptiTime, and the two rewrote the row every minute).
                    const ownFinish = await requestServerFinish(ticket.id, 'pause_extend', {
                      reason: 'opti_finish_pushback_paused_chain',
                      params: { when: 'finish_drifted' },
                    });
                    if (!ownFinish.success && !ownFinish.refused) throw new Error(ownFinish.error || 'server finish failed');
                    if (ticket.shop_id) await cascadeRolloverChainAfterPushback(ticket.id, ticket.shop_id);
                    console.log(`[OptiFinishPushback] Paused chain ticket ${ticket.id}: finish +${driftMin}min, chain re-planned`);
                  } catch (propErr) {
                    // FIX-R132-MED-6: chain-propagation throw is reported here too.
                    reportPushbackFailure('chain propagation failed (paused)', ticket.id, ticket.shop_id ?? null, propErr);
                    // CSI-I2-A2 → Core-100 R4 (CORE-AC): the chain-fallback asks the server again.
                    // Preserves the existing fallback semantics — when the chain re-plan throws
                    // (chain integrity issue), the projected finish is still recorded on this
                    // ticket so the board doesn't appear stuck; the post-update
                    // triggerBoardCascade below handles downstream.
                    const fallbackMutate = await requestServerFinish(ticket.id, 'pause_extend', {
                      reason: 'opti_finish_pushback_paused_chain_fallback',
                      params: { when: 'finish_drifted' },
                    });
                    if (!fallbackMutate.success && !fallbackMutate.refused) {
                      reportPushbackFailure(
                        'paused chain-fallback raw update failed',
                        ticket.id,
                        ticket.shop_id ?? null,
                        new Error(fallbackMutate.error || 'mutate failed'),
                      );
                    }
                  }
                } else {
                  // CSI-I2-A2 → Core-100 R4 (CORE-AC): the no-start-time fallback asks the server
                  // for the paused finish of this ticket alone (no chain propagation without a start).
                  const fallbackMutate = await requestServerFinish(ticket.id, 'pause_extend', {
                    reason: 'opti_finish_pushback_paused_no_start_time_fallback',
                    params: { when: 'finish_drifted' },
                  });
                  if (!fallbackMutate.success && !fallbackMutate.refused) {
                    reportPushbackFailure(
                      'paused no-start-time raw update failed',
                      ticket.id,
                      ticket.shop_id ?? null,
                      new Error(fallbackMutate.error || 'mutate failed'),
                    );
                  }
                }
              } else {
                // Non-chain ticket: V-A-6 → Core-100 R4 (CORE-AC) the server's 'pause_extend'
                // derivation. The bespoke triggerBoardCascade call below (scoped by tech +
                // ticketDateStr) still follows.
                const mutateResult = await requestServerFinish(ticket.id, 'pause_extend', {
                  reason: 'opti_finish_pushback_paused_nonchain',
                  params: { when: 'finish_drifted' },
                });

                if (!mutateResult.success && !mutateResult.refused) {
                  // FIX-R132-MED-6: also push to client-error buffer.
                  reportPushbackFailure('paused non-chain ticket update failed', ticket.id, ticket.shop_id ?? null, new Error(mutateResult.error || 'mutate failed'));
                }
              }

              // Cascade downstream tickets regardless of path
              try {
                const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
                const ticketShopId = ticket.shop_id;
                const ticketDateStr = ticket.scheduled_date ||
                  (ticket.start_time ? getShopDayLabel(ticket.start_time, timezone) : dateStr);
                if (ticketShopId && ticketDateStr) {
                  await triggerBoardCascade(ticketShopId, ticketDateStr, ticket.assigned_tech_id);
                }
                console.log(`[OptiFinishPushback] Paused ticket ${ticket.id}: pushed opti_finish +${driftMin}min, cascaded downstream`);
              } catch (cascadeError) {
                console.error('[OptiFinishPushback] Error cascading after paused pushback:', cascadeError);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error checking opti_finish pushback:', error);
      }
    };

    // Check immediately on mount (cold-load eagerness preserved)
    checkAndPushbackOptiFinish();

    // VIS-A1: schedule recurring tick at phase +20s relative to AutoBump
    // (offset 0). Steady-state cycle: AutoBump @ 60/120/180s, this hook @
    // 80/140/200s, ConvertedRolloverGrowth @ 100/160/220s. The trio's three
    // shop_profile + tickets reads + cascade calls now spread across 60s
    // instead of bunching in the same millisecond.
    cleanupTick = createStaggeredInterval(
      checkAndPushbackOptiFinish,
      60_000,
      VIS_A1_STAGGER.OPTI_FINISH_PUSHBACK,
    );

    return () => {
      cleanupTick?.();
      cleanupTick = null;
    };
  // R5-M-1: shopId in deps so effect reruns when auth resolves
  }, [selectedDayLabel, shopId, toast]);
};
